/**
 * Common masking utility functions for Name, Email, and Contact/Phone
 * Masking behavior is governed by UserData in localStorage:
 * - Enabled if UserData.permissions.masking.permissions === "true" || true
 * - Masking levels from UserData.masking_levels:
 *   The numeric value indicates how many characters to display at the start and end:
 *   - value "4" => 4 characters visible at start & 4 at end
 *   - value "3" => 3 characters visible at start & 3 at end
 *   - value "2" => 2 characters visible at start & 2 at end
 *   - value "0" => masking disabled (shows full data)
 */

/**
 * Check if masking is enabled for the current logged-in user
 */
export const isMaskingEnabled = () => {
    try {
        const userData = JSON.parse(localStorage.getItem('UserData'));
        const maskingPerm = userData?.permissions?.masking?.permissions;
        return maskingPerm === "true" || maskingPerm === true;
    } catch (e) {
        return false;
    }
};

/**
 * Retrieve the masking level for a given key from UserData.masking_levels
 * @param {'masking_name_level' | 'masking_email_level' | 'masking_contact_level'} keyName
 * @returns {number} The numeric count of characters to show (e.g. 4, 3, 2, or 0 if disabled)
 */
export const getMaskingLevel = (keyName) => {
    try {
        const userData = JSON.parse(localStorage.getItem('UserData'));
        const levelObj = userData?.masking_levels?.find(
            (item) => item?.key_name === keyName
        );
        if (levelObj?.value !== undefined && levelObj?.value !== null) {
            const parsed = parseInt(levelObj.value, 10);
            return isNaN(parsed) ? 2 : parsed;
        }
        return 0;
    } catch (e) {
        return 0;
    }
};

/**
 * Mask a person's name (first name, last name, or full name)
 * Strictly shows `count` characters and replaces all remaining characters with asterisks.
 * If word length <= count, the word remains unmasked.
 *
 * Examples with level = 4:
 * "Alexander Wank" => "Alex***** Wank"
 * Examples with level = 3:
 * "Abhi oct" => "Abh* oct"
 * "Abhii oct" => "Abh** oct"
 * "Alexander Wank" => "Ale****** *ank"
 *
 * @param {string} name
 * @param {string|number} [customLevel] Optional override for level
 * @param {boolean} [isLastName] Optional flag indicating single-word name is a last name
 * @returns {string}
 */
export const maskName = (name, customLevel, isLastName = false) => {
    if (!name || typeof name !== 'string') return name;
    if (!isMaskingEnabled()) return name;

    const count = customLevel !== undefined ? parseInt(customLevel, 10) : getMaskingLevel("masking_name_level");
    if (isNaN(count) || count <= 0) return name;

    const words = name.trim().split(/\s+/);
    if (words.length === 0 || (words.length === 1 && words[0] === "")) return name;

    const maskSingleWord = (word, asLastName) => {
        const len = word.length;
        if (len <= count) {
            return word;
        }
        const visibleCount = count;
        const maskCount = len - visibleCount;

        if (asLastName) {
            // Asterisks at start, last visibleCount characters shown
            return "*".repeat(maskCount) + word.slice(-visibleCount);
        } else {
            // First visibleCount characters shown, asterisks at end
            return word.slice(0, visibleCount) + "*".repeat(maskCount);
        }
    };

    if (words.length === 1) {
        return maskSingleWord(words[0], isLastName);
    }

    const maskedWords = words.map((word, index) => {
        if (index === 0) {
            return maskSingleWord(word, false);
        } else if (index === words.length - 1) {
            return maskSingleWord(word, true);
        } else {
            // Middle words: strictly show count characters and star remaining
            return maskSingleWord(word, false);
        }
    });

    return maskedWords.join(" ");
};

/**
 * Mask a last name
 * @param {string} lastName
 * @param {string|number} [customLevel]
 * @returns {string}
 */
export const maskLastName = (lastName, customLevel) => {
    return maskName(lastName, customLevel, true);
};

/**
 * Mask an email address
 * Dynamically shows `count` characters at both start and end.
 *
 * Examples with "alexander@gmail.com":
 * - value 4: "alex***********.com" (4 at start & 4 at end)
 * - value 3: "ale*************com" (3 at start & 3 at end)
 * - value 2: "al***************om" (2 at start & 2 at end)
 *
 * @param {string} email
 * @param {string|number} [customLevel] Optional override for level
 * @returns {string}
 */
export const maskEmail = (email, customLevel) => {
    if (!email || typeof email !== 'string' || !email.includes('@')) return email;
    if (!isMaskingEnabled()) return email;

    const count = customLevel !== undefined ? parseInt(customLevel, 10) : getMaskingLevel("masking_email_level");
    if (isNaN(count) || count <= 0) return email;

    const len = email.length;
    if (len <= 2) return "*".repeat(len);

    let startCount = count;
    let endCount = count;

    if (len <= startCount + endCount) {
        startCount = Math.max(1, Math.floor((len - 1) / 2));
        endCount = Math.max(1, Math.floor((len - 1) / 2));
    }

    const firstChars = email.slice(0, startCount);
    const lastChars = email.slice(-endCount);
    const middleAsterisks = "*".repeat(Math.max(1, len - (startCount + endCount)));

    return `${firstChars}${middleAsterisks}${lastChars}`;
};

/**
 * Mask a contact / phone number
 * Dynamically shows `count` digits at both start and end (preserving country code / prefix).
 *
 * Examples:
 * - With value 4:
 *   "9876543210" => "9876**3210"
 *   "+91-9876543210" => "+91-9876**3210"
 * - With value 3:
 *   "9876543210" => "987****210"
 *   "+91-9876543210" => "+91-987****210"
 *
 * @param {string} contact
 * @param {string|number} [customLevel] Optional override for level
 * @returns {string}
 */
export const maskContact = (contact, customLevel) => {
    if (!contact || typeof contact !== 'string') return contact;
    const trimmed = contact.trim();
    if (!trimmed || trimmed === '-' || trimmed.toLowerCase() === 'n/a') return contact;
    if (!isMaskingEnabled()) return contact;

    const count = customLevel !== undefined ? parseInt(customLevel, 10) : getMaskingLevel("masking_contact_level");
    if (isNaN(count) || count <= 0) return contact;

    let prefix = "";
    let numberPart = trimmed;

    if (trimmed.includes('-')) {
        const lastDashIndex = trimmed.lastIndexOf('-');
        prefix = trimmed.substring(0, lastDashIndex + 1);
        numberPart = trimmed.substring(lastDashIndex + 1);
    } else if (trimmed.startsWith('+')) {
        const spaceIndex = trimmed.indexOf(' ');
        if (spaceIndex !== -1) {
            prefix = trimmed.substring(0, spaceIndex + 1);
            numberPart = trimmed.substring(spaceIndex + 1);
        }
    }

    const len = numberPart.length;
    if (len <= 2) {
        return prefix + "*".repeat(len);
    }

    let startCount = count;
    let endCount = count;

    if (len <= startCount + endCount) {
        startCount = Math.max(1, Math.floor((len - 1) / 2));
        endCount = Math.max(1, Math.floor((len - 1) / 2));
    }

    const firstChars = numberPart.slice(0, startCount);
    const lastChars = numberPart.slice(-endCount);
    const middleAsterisks = "*".repeat(Math.max(1, len - (startCount + endCount)));

    return `${prefix}${firstChars}${middleAsterisks}${lastChars}`;
};
